import React, { useEffect, useState } from 'react';
import Icon from 'components/AppIcon';
import clientPortalService from 'services/clientPortalService';

const InvoiceViewer = ({ language, userId }) => {
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const invoicesData = await clientPortalService?.getInvoices(userId);
        const formattedInvoices = invoicesData?.map(inv => ({
          id: inv?.id,
          caseId: inv?.caseId,
          caseTitle: inv?.caseTitle,
          caseTitleSw: inv?.caseTitle,
          amount: inv?.amount,
          currency: inv?.currency,
          issueDate: new Date(inv?.issueDate)?.toLocaleDateString('en-GB'),
          dueDate: new Date(inv?.dueDate)?.toLocaleDateString('en-GB'),
          status: inv?.status,
          lawyer: inv?.lawyer?.fullName || 'Lawyer',
          description: inv?.description,
          descriptionSw: inv?.description,
          items: inv?.items || [],
          paymentMethods: inv?.paymentMethods || [],
          paidDate: inv?.paidDate ? new Date(inv?.paidDate)?.toLocaleDateString('en-GB') : null,
          paymentMethod: inv?.paymentMethod
        })) || [];
        setInvoices(formattedInvoices);
      } catch (error) {
        console.error('Error fetching invoices:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [userId]);

  const translations = {
    en: {
      invoices: "Invoices",
      invoiceDetails: "Invoice Details",
      allInvoices: "All Invoices",
      pending: "Pending",
      paid: "Paid",
      overdue: "Overdue",
      cancelled: "Cancelled",
      invoiceNumber: "Invoice Number",
      caseReference: "Case Reference",
      issueDate: "Issue Date",
      dueDate: "Due Date",
      amount: "Amount",
      status: "Status",
      description: "Description",
      quantity: "Qty",
      rate: "Rate",
      total: "Total",
      subtotal: "Subtotal",
      tax: "Tax (16%)",
      grandTotal: "Grand Total",
      payNow: "Pay Now",
      downloadPDF: "Download PDF",
      paymentMethods: "Payment Methods",
      bankTransfer: "Bank Transfer",
      mpesa: "M-Pesa",
      cheque: "Cheque",
      paidOn: "Paid on",
      via: "via",
      noInvoices: "No invoices found",
      selectInvoice: "Select an invoice to view details",
      backToList: "Back to List"
    },
    sw: {
      invoices: "Bili",
      invoiceDetails: "Maelezo ya Bili",
      allInvoices: "Bili Zote",
      pending: "Inasubiri",
      paid: "Imelipwa",
      overdue: "Imechelewa",
      cancelled: "Imeghairiwa",
      invoiceNumber: "Nambari ya Bili",
      caseReference: "Rejea ya Kesi",
      issueDate: "Tarehe ya Kutoa",
      dueDate: "Tarehe ya Kulipa",
      amount: "Kiasi",
      status: "Hali",
      description: "Maelezo",
      quantity: "Idadi",
      rate: "Kiwango",
      total: "Jumla",
      subtotal: "Jumla Ndogo",
      tax: "Ushuru (16%)",
      grandTotal: "Jumla Kubwa",
      payNow: "Lipa Sasa",
      downloadPDF: "Pakua PDF",
      paymentMethods: "Njia za Malipo",
      bankTransfer: "Uhamisho wa Benki",
      mpesa: "M-Pesa",
      cheque: "Hundi",
      paidOn: "Imelipwa tarehe",
      via: "kupitia",
      noInvoices: "Hakuna bili zilizopatikana",
      selectInvoice: "Chagua bili ili kuona maelezo",
      backToList: "Rudi kwenye Orodha"
    }
  };

  const t = translations?.[language];

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-success-100 text-success-700 border-success-200';
      case 'pending':
        return 'bg-warning-100 text-warning-700 border-warning-200';
      case 'overdue':
        return 'bg-error-100 text-error-700 border-error-200';
      case 'cancelled':
        return 'bg-secondary-100 text-secondary-700 border-secondary-200';
      default:
        return 'bg-secondary-100 text-secondary-700 border-secondary-200';
    }
  };

  const formatCurrency = (amount, currency = 'KES') => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const filteredInvoices = filterStatus === 'all' 
    ? invoices 
    : invoices?.filter(invoice => invoice?.status === filterStatus);

  const handlePayInvoice = (invoice) => {
    // Mock payment functionality
    console.log('Processing payment for invoice:', invoice?.id);
  };

  const handleDownloadPDF = (invoice) => {
    // Mock PDF download functionality
    console.log('Downloading PDF for invoice:', invoice?.id);
  };

  const calculateTax = (subtotal) => {
    return subtotal * 0.16; // 16% VAT
  };

  const renderInvoiceList = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'all', label: t?.allInvoices },
            { id: 'pending', label: t?.pending },
            { id: 'paid', label: t?.paid },
            { id: 'overdue', label: t?.overdue }
          ]?.map(filter => (
            <button
              key={filter?.id}
              onClick={() => setFilterStatus(filter?.id)}
              className={`
                px-4 py-2 rounded-lg font-medium transition-smooth
                ${filterStatus === filter?.id
                  ? 'bg-primary text-white' :'bg-secondary-100 text-text-secondary hover:bg-secondary-200'
                }
              `}
            >
              {filter?.label}
            </button>
          ))}
        </div>
        {/* Invoices List */}
        {filteredInvoices?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Receipt" size={48} className="mx-auto text-text-secondary mb-4" />
            <p className="text-text-secondary">{t?.noInvoices}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredInvoices?.map(invoice => (
              <div
                key={invoice?.id}
                className="bg-surface border border-border rounded-lg p-6 hover:shadow-card transition-smooth cursor-pointer"
                onClick={() => setSelectedInvoice(invoice)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-medium text-text-primary">{invoice?.id}</h3>
                    <p className="text-sm text-text-secondary mt-1">
                      {language === 'en' ? invoice?.caseTitle : invoice?.caseTitleSw}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(invoice?.status)}`}>
                    {t?.[invoice?.status]}
                  </span>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm mb-4">
                  <div>
                    <span className="text-text-secondary">{t?.issueDate}:</span>
                    <p className="font-medium text-text-primary">{invoice?.issueDate}</p>
                  </div>
                  <div>
                    <span className="text-text-secondary">{t?.dueDate}:</span>
                    <p className="font-medium text-text-primary">{invoice?.dueDate}</p>
                  </div>
                  <div>
                    <span className="text-text-secondary">{t?.amount}:</span>
                    <p className="font-medium text-text-primary">{formatCurrency(invoice?.amount)}</p>
                  </div>
                  <div>
                    <span className="text-text-secondary">Lawyer:</span>
                    <p className="font-medium text-text-primary">{invoice?.lawyer}</p>
                  </div>
                </div>

                {invoice?.status === 'paid' && (
                  <div className="flex items-center gap-2 text-sm text-success-700 bg-success-50 px-3 py-2 rounded">
                    <Icon name="CheckCircle" size={16} />
                    <span>{t?.paidOn} {invoice?.paidDate} {t?.via} {invoice?.paymentMethod}</span>
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-text-secondary">
                    {language === 'en' ? invoice?.description : invoice?.descriptionSw}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleDownloadPDF(invoice);
                      }}
                      className="px-3 py-1 bg-secondary-100 text-text-secondary rounded hover:bg-secondary-200 transition-smooth"
                    >
                      <Icon name="Download" size={16} />
                    </button>
                    {invoice?.status === 'pending' || invoice?.status === 'overdue' ? (
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handlePayInvoice(invoice);
                        }}
                        className="px-3 py-1 bg-primary text-white rounded hover:bg-primary-700 transition-smooth"
                      >
                        {t?.payNow}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderInvoiceDetails = () => {
    if (!selectedInvoice) return null;

    const subtotal = selectedInvoice?.items?.reduce((sum, item) => sum + item?.amount, 0);
    const tax = calculateTax(subtotal);
    const grandTotal = subtotal + tax;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelectedInvoice(null)}
            className="flex items-center gap-2 text-primary hover:text-primary-700 transition-smooth"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>{t?.backToList}</span>
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => handleDownloadPDF(selectedInvoice)}
              className="flex items-center gap-2 px-4 py-2 bg-secondary-100 text-text-secondary rounded-lg hover:bg-secondary-200 transition-smooth"
            >
              <Icon name="Download" size={16} />
              <span>{t?.downloadPDF}</span>
            </button>
            {(selectedInvoice?.status === 'pending' || selectedInvoice?.status === 'overdue') && (
              <button
                onClick={() => handlePayInvoice(selectedInvoice)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth"
              >
                <Icon name="CreditCard" size={16} />
                <span>{t?.payNow}</span>
              </button>
            )}
          </div>
        </div>
        {/* Invoice Details */}
        <div className="bg-surface rounded-lg shadow-card p-8">
          {/* Invoice Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-2xl font-heading font-bold text-text-primary mb-2">
                {selectedInvoice?.id}
              </h1>
              <p className="text-text-secondary">
                {language === 'en' ? selectedInvoice?.caseTitle : selectedInvoice?.caseTitleSw}
              </p>
            </div>
            <span className={`px-4 py-2 rounded-full font-medium border ${getStatusColor(selectedInvoice?.status)}`}>
              {t?.[selectedInvoice?.status]}
            </span>
          </div>

          {/* Invoice Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div>
              <h3 className="font-medium text-text-primary mb-2">{t?.invoiceNumber}</h3>
              <p className="text-text-secondary">{selectedInvoice?.id}</p>
            </div>
            <div>
              <h3 className="font-medium text-text-primary mb-2">{t?.issueDate}</h3>
              <p className="text-text-secondary">{selectedInvoice?.issueDate}</p>
            </div>
            <div>
              <h3 className="font-medium text-text-primary mb-2">{t?.dueDate}</h3>
              <p className="text-text-secondary">{selectedInvoice?.dueDate}</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto mb-8">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 font-medium text-text-primary">{t?.description}</th>
                  <th className="text-center py-3 font-medium text-text-primary">{t?.quantity}</th>
                  <th className="text-right py-3 font-medium text-text-primary">{t?.rate}</th>
                  <th className="text-right py-3 font-medium text-text-primary">{t?.amount}</th>
                </tr>
              </thead>
              <tbody>
                {selectedInvoice?.items?.map((item, index) => (
                  <tr key={index} className="border-b border-border">
                    <td className="py-3 text-text-primary">
                      {language === 'en' ? item?.description : item?.descriptionSw}
                    </td>
                    <td className="py-3 text-center text-text-secondary">{item?.quantity}</td>
                    <td className="py-3 text-right text-text-secondary">{formatCurrency(item?.rate)}</td>
                    <td className="py-3 text-right font-medium text-text-primary">{formatCurrency(item?.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-full lg:w-1/3 space-y-2">
              <div className="flex justify-between py-2">
                <span className="text-text-secondary">{t?.subtotal}:</span>
                <span className="font-medium text-text-primary">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-text-secondary">{t?.tax}:</span>
                <span className="font-medium text-text-primary">{formatCurrency(tax)}</span>
              </div>
              <div className="flex justify-between py-2 border-t border-border">
                <span className="font-medium text-text-primary">{t?.grandTotal}:</span>
                <span className="font-bold text-lg text-text-primary">{formatCurrency(grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          {(selectedInvoice?.status === 'pending' || selectedInvoice?.status === 'overdue') && (
            <div className="mt-8 pt-8 border-t border-border">
              <h3 className="font-medium text-text-primary mb-4">{t?.paymentMethods}</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {selectedInvoice?.paymentMethods?.includes('mpesa') && (
                  <div className="flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-secondary-50 cursor-pointer transition-smooth">
                    <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center">
                      <Icon name="Smartphone" size={20} className="text-success-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary">{t?.mpesa}</h4>
                      <p className="text-sm text-text-secondary">Pay via mobile money</p>
                    </div>
                  </div>
                )}
                {selectedInvoice?.paymentMethods?.includes('bank_transfer') && (
                  <div className="flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-secondary-50 cursor-pointer transition-smooth">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Icon name="Building" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary">{t?.bankTransfer}</h4>
                      <p className="text-sm text-text-secondary">Direct bank transfer</p>
                    </div>
                  </div>
                )}
                {selectedInvoice?.paymentMethods?.includes('cheque') && (
                  <div className="flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-secondary-50 cursor-pointer transition-smooth">
                    <div className="w-10 h-10 bg-warning-100 rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={20} className="text-warning-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary">{t?.cheque}</h4>
                      <p className="text-sm text-text-secondary">Bank cheque payment</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-text-primary">
          {selectedInvoice ? t?.invoiceDetails : t?.invoices}
        </h1>
      </div>
      <div className="bg-surface rounded-lg shadow-card p-6">
        {selectedInvoice ? renderInvoiceDetails() : renderInvoiceList()}
      </div>
    </div>
  );
};

export default InvoiceViewer;